import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';
import AdminLayout from '../../components/AdminLayout/AdminLayout';
import { API_BASE_URL } from '../../config/api';
import * as messageService from '../../services/messageService';
import './Message2Page.css';

const Message2Page = () => {
  const { user } = useAuth();
  const { on, off, joinConversation, leaveConversation } = useSocket();
  const [searchParams, setSearchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedConv, setSelectedConv] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [autoOpenProcessed, setAutoOpenProcessed] = useState(false);

  const currentUserId = user?.id;
  const chatEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const prevConvIdRef = useRef(null);
  const selectedConvRef = useRef(null);
  const prevMessageCountRef = useRef(0);
  const isUserScrolledUpRef = useRef(false);

  // Keep ref in sync
  useEffect(() => { selectedConvRef.current = selectedConv; }, [selectedConv]);

  // Track if user has scrolled up in the chat container
  useEffect(() => {
    const container = chatContainerRef.current;
    if (!container) return;
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      isUserScrolledUpRef.current = scrollHeight - scrollTop - clientHeight > 80;
    };
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [selectedConv?.id]);

  // Smart scroll: only on new messages, not on initial load/switch
  useEffect(() => {
    if (!chatMessages || chatMessages.length === 0) {
      prevMessageCountRef.current = 0;
      return;
    }
    const prevCount = prevMessageCountRef.current;
    prevMessageCountRef.current = chatMessages.length;

    // On chat switch (count went from 0 to N), scroll instantly to bottom
    if (prevCount === 0 && chatMessages.length > 0) {
      requestAnimationFrame(() => {
        const container = chatContainerRef.current;
        if (container) container.scrollTop = container.scrollHeight;
      });
      isUserScrolledUpRef.current = false;
      return;
    }
    // New message arrived — scroll smoothly only if user is near bottom
    if (chatMessages.length > prevCount && !isUserScrolledUpRef.current) {
      requestAnimationFrame(() => {
        const container = chatContainerRef.current;
        if (container) {
          container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
        }
      });
    }
  }, [chatMessages]);

  // Fetch all conversations (admin gets all)
  const fetchConversations = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const data = await messageService.getAdminConversations();
      setConversations(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching admin conversations:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchConversations(); }, [fetchConversations]);

  // Auto-open conversation when ?seller=<id> or ?chatId=<id> is in URL
  useEffect(() => {
    if (autoOpenProcessed || loading || conversations.length === 0) return;

    const sellerParam = searchParams.get('seller');
    const chatIdParam = searchParams.get('chatId');

    if (chatIdParam) {
      const targetId = parseInt(chatIdParam, 10);
      const match = conversations.find(c => c.id === targetId);
      if (match) {
        handleSelectConv(match);
        setAutoOpenProcessed(true);
        setSearchParams({}, { replace: true });
      }
      return;
    }

    if (sellerParam) {
      const sellerId = parseInt(sellerParam, 10);
      const sellerUserId = searchParams.get('userId') ? parseInt(searchParams.get('userId'), 10) : null;
      // Look for existing conversation involving this seller's user ID or seller table ID
      const match = conversations.find(c =>
        c.sellerId === sellerId ||
        c.seller?.id === sellerId ||
        (sellerUserId && (c.sellerId === sellerUserId || c.seller?.id === sellerUserId)) ||
        c.buyerId === sellerId
      );

      if (match) {
        handleSelectConv(match);
        setAutoOpenProcessed(true);
        setSearchParams({}, { replace: true });
      } else {
        // No existing conversation found — create one
        const createConv = async () => {
          try {
            const result = await messageService.createConversation(
              currentUserId,  // admin as buyer
              sellerId,        // seller ID (backend maps to userId)
              'general',
              null,
              null
            );
            if (result?.id) {
              await fetchConversations();
              // The conversation will be found in the next render cycle
            }
          } catch (err) {
            console.error('Failed to create conversation with seller:', err);
            alert('Failed to start chat with this supplier. Please try again.');
          } finally {
            setAutoOpenProcessed(true);
            setSearchParams({}, { replace: true });
          }
        };
        createConv();
      }
    }
  }, [conversations, loading, autoOpenProcessed, searchParams, currentUserId, fetchConversations, setSearchParams]);

  // After fetchConversations completes from createConv, auto-select the new conversation
  useEffect(() => {
    if (!autoOpenProcessed || selectedConv) return;
    // If we just created a conversation, pick the most recent one
    if (conversations.length > 0 && !selectedConv) {
      const newest = conversations[0]; // conversations are sorted by updatedAt desc
      handleSelectConv(newest);
    }
  }, [conversations, autoOpenProcessed, selectedConv]);

  // Polling: refresh conversations every 6 seconds
  useEffect(() => {
    const interval = setInterval(() => fetchConversations(true), 6000);
    return () => clearInterval(interval);
  }, [fetchConversations]);

  // Polling: refresh active chat messages every 5 seconds
  useEffect(() => {
    if (!selectedConv) return;
    const interval = setInterval(async () => {
      try {
        const data = await messageService.getMessages(selectedConv.id, currentUserId);
        if (data?.messages) setChatMessages(data.messages);
      } catch { /* ignore */ }
    }, 5000);
    return () => clearInterval(interval);
  }, [selectedConv?.id, currentUserId]);

  // ============ Socket.IO real-time ============
  useEffect(() => {
    if (!on || !off) return;

    const handleNewMessage = ({ conversationId, message }) => {
      const current = selectedConvRef.current;
      if (current && current.id === conversationId) {
        setChatMessages(prev => {
          if (prev.find(m => m.id === message.id)) return prev;
          return [...prev, message];
        });
      }
      fetchConversations(true);
    };

    const handleConvUpdated = () => {
      fetchConversations(true);
    };

    on('new_message', handleNewMessage);
    on('conversation_updated', handleConvUpdated);
    on('new_conversation', handleConvUpdated);

    return () => {
      off('new_message', handleNewMessage);
      off('conversation_updated', handleConvUpdated);
      off('new_conversation', handleConvUpdated);
    };
  }, [on, off, fetchConversations]);

  // Join/leave conversation room for real-time
  useEffect(() => {
    if (prevConvIdRef.current && prevConvIdRef.current !== selectedConv?.id) {
      leaveConversation(prevConvIdRef.current);
    }
    if (selectedConv?.id) {
      joinConversation(selectedConv.id);
      prevConvIdRef.current = selectedConv.id;
    }
    return () => {
      if (prevConvIdRef.current) leaveConversation(prevConvIdRef.current);
    };
  }, [selectedConv?.id, joinConversation, leaveConversation]);

  // Select a conversation and load messages
  const handleSelectConv = async (conv) => {
    setSelectedConv(conv);
    try {
      const data = await messageService.getMessages(conv.id, currentUserId);
      if (data?.messages) {
        setChatMessages(data.messages);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  // Send message as admin
  const handleSendMessage = async () => {
    if (!messageText.trim() || !selectedConv) return;
    setSendingMessage(true);
    try {
      const sentMsg = await messageService.sendMessage(selectedConv.id, currentUserId, messageText.trim());
      if (sentMsg) {
        setChatMessages(prev => [...prev, { ...sentMsg, isOwn: true }]);
      }
      setMessageText('');
      fetchConversations(true);
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSendingMessage(false);
    }
  };

  // Delete conversation (admin)
  const handleDeleteConversation = async (e, convId) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to permanently delete this conversation? This cannot be undone.')) return;
    try {
      await messageService.adminDeleteConversation(convId);
      if (selectedConv?.id === convId) {
        setSelectedConv(null);
        setChatMessages([]);
      }
      fetchConversations();
    } catch (error) {
      console.error('Error deleting conversation:', error);
      alert('Failed to delete conversation');
    }
  };

  const getAvatarUrl = (user) => {
    const avatar = user?.avatar;
    if (!avatar) return null;
    if (avatar.startsWith('http')) return avatar;
    return `${API_BASE_URL}${avatar}`;
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const diff = now - d;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days === 0) return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatMsgTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const getBuyerName = (conv) => conv.buyer?.companyName || conv.buyer?.email || 'Buyer';
  const getSellerName = (conv) => conv.seller?.companyName || conv.seller?.email || 'Seller';

  // Filter conversations by search and type
  const filteredConversations = conversations.filter(conv => {
    if (typeFilter !== 'all' && conv.type !== typeFilter) return false;
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return getBuyerName(conv).toLowerCase().includes(q) || getSellerName(conv).toLowerCase().includes(q);
  });

  return (
    <AdminLayout>
      <div className="message2-content">
        {/* Inbox Panel */}
        <div className="inbox-panel">
          <div className="inbox-header">
            <h2>All Chats</h2>
            <span style={{ fontSize: 14, color: '#6D6D6D' }}>{filteredConversations.length} of {conversations.length}</span>
          </div>
          <div style={{ padding: '8px 16px', borderBottom: '1px solid #E6E6E6', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <input
              type="text"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
            />
            <div style={{ display: 'flex', gap: 6 }}>
              {['all', 'inquiry', 'general'].map(t => (
                <button
                  key={t}
                  onClick={() => setTypeFilter(t)}
                  style={{
                    padding: '4px 12px',
                    borderRadius: 16,
                    border: typeFilter === t ? '1px solid #1F396C' : '1px solid #ddd',
                    background: typeFilter === t ? '#1F396C' : '#fff',
                    color: typeFilter === t ? '#fff' : '#666',
                    fontSize: 12,
                    fontWeight: 500,
                    cursor: 'pointer',
                    textTransform: 'capitalize'
                  }}
                >
                  {t === 'all' ? 'All' : t === 'inquiry' ? '📩 Inquiries' : '💬 General'}
                </button>
              ))}
            </div>
          </div>
          <div className="inbox-list">
            {loading ? (
              <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8', width: '100%' }}>Loading conversations...</div>
            ) : filteredConversations.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8', width: '100%' }}>No conversations found</div>
            ) : (
              filteredConversations.map((conv) => (
                <div
                  key={conv.id}
                  className={`inbox-item ${selectedConv?.id === conv.id ? 'active' : ''}`}
                  onClick={() => handleSelectConv(conv)}
                >
                  {getAvatarUrl(conv.buyer) ? (
                    <img src={getAvatarUrl(conv.buyer)} alt="" style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                  ) : null}
                  <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#e2e8f0', display: getAvatarUrl(conv.buyer) ? 'none' : 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 16, fontWeight: 600, color: '#64748b' }}>
                    {getBuyerName(conv).charAt(0).toUpperCase()}
                  </div>
                  <div className="inbox-item-content">
                    <div className="inbox-item-header">
                      <div className="inbox-item-name">
                        <span className="name" style={{ fontSize: 14 }}>{getBuyerName(conv)}</span>
                        <span className="role" style={{ fontSize: 11, marginLeft: 4 }}>↔ {getSellerName(conv)}</span>
                        {conv.type === 'inquiry' && (
                          <span style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 8, background: '#DBEAFE', color: '#1E40AF', fontSize: 10, fontWeight: 600 }}>INQUIRY</span>
                        )}
                      </div>
                      <div className="inbox-item-meta">
                        <span className="time" style={{ fontSize: 11 }}>{conv.time || formatTime(conv.updatedAt)}</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <p className="inbox-item-preview" style={{ fontSize: 12 }}>
                        {conv.preview
                          ? (conv.preview.length > 40 ? conv.preview.substring(0, 40) + '...' : conv.preview)
                          : `${conv._count?.messages || 0} messages · ${conv.type || 'general'}`
                        }
                      </p>
                      <button
                        onClick={(e) => handleDeleteConversation(e, conv.id)}
                        title="Delete conversation"
                        style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 4, color: '#94a3b8', borderRadius: 4, display: 'flex', alignItems: 'center' }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.background = '#fef2f2'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = '#94a3b8'; e.currentTarget.style.background = 'none'; }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Chat Panel */}
        <div className="chat-panel">
          {!selectedConv ? (
            <div className="chat-empty-state">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
              <h3>Admin Chat Management</h3>
              <p>Select a conversation to view messages</p>
            </div>
          ) : (
            <>
              <div className="chat-header" style={{ justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  {getAvatarUrl(selectedConv.buyer) ? (
                    <img src={getAvatarUrl(selectedConv.buyer)} alt="" style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover' }} onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                  ) : null}
                  <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#e2e8f0', display: getAvatarUrl(selectedConv.buyer) ? 'none' : 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, color: '#64748b' }}>
                    {getBuyerName(selectedConv).charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontWeight: 500, color: '#1e293b' }}>{getBuyerName(selectedConv)}</div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>↔ {getSellerName(selectedConv)} · {selectedConv.type || 'general'}</div>
                  </div>
                </div>
                <button
                  onClick={(e) => handleDeleteConversation(e, selectedConv.id)}
                  style={{ border: '1px solid #fca5a5', background: '#fff', color: '#ef4444', padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
                  </svg>
                  Delete
                </button>
              </div>
              <div className="chat-messages" ref={chatContainerRef} style={{ background: '#f8fafc' }}>
                {chatMessages.length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#94a3b8', padding: 32, width: '100%', margin: 'auto' }}>No messages in this conversation</div>
                ) : (
                  chatMessages.map((msg, idx) => (
                    <div key={msg.id || idx} className={`chat-message ${msg.isOwn ? 'user' : 'other'}`} style={{ justifyContent: msg.isOwn ? 'flex-end' : 'flex-start' }}>
                      <div className="message-content" style={{ alignItems: msg.isOwn ? 'flex-end' : 'flex-start', flex: 'none', maxWidth: '70%' }}>
                        <div className="message-bubble" style={{ background: msg.isOwn ? '#dcfce7' : '#fff', borderColor: msg.isOwn ? '#bbf7d0' : '#e6e6e6' }}>
                          {msg.type === 'image' && msg.attachmentUrl ? (
                            <div>
                              <img
                                src={msg.attachmentUrl.startsWith('http') ? msg.attachmentUrl : `${API_BASE_URL}${msg.attachmentUrl}`}
                                alt="Shared"
                                style={{ maxWidth: 220, borderRadius: 4, cursor: 'pointer', display: 'block' }}
                                onClick={() => window.open(msg.attachmentUrl.startsWith('http') ? msg.attachmentUrl : `${API_BASE_URL}${msg.attachmentUrl}`, '_blank')}
                              />
                              {msg.content && msg.content !== msg.attachmentName && (
                                <p style={{ margin: '6px 0 0', fontSize: 13, color: '#334155' }}>{msg.content}</p>
                              )}
                            </div>
                          ) : msg.type === 'file' && msg.attachmentUrl ? (
                            <div>
                              <a
                                href={msg.attachmentUrl.startsWith('http') ? msg.attachmentUrl : `${API_BASE_URL}${msg.attachmentUrl}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ display: 'flex', gap: 8, alignItems: 'center', textDecoration: 'none', color: '#334155' }}
                              >
                                <div style={{ width: 36, height: 36, borderRadius: 6, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="1.5">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
                                  </svg>
                                </div>
                                <div>
                                  <div style={{ fontSize: 12, fontWeight: 500 }}>{msg.attachmentName || 'File'}</div>
                                  {msg.attachmentSize && <div style={{ fontSize: 11, color: '#94a3b8' }}>{(msg.attachmentSize / 1024).toFixed(0)} KB</div>}
                                </div>
                              </a>
                              {msg.content && msg.content !== msg.attachmentName && (
                                <p style={{ margin: '6px 0 0', fontSize: 13, color: '#334155' }}>{msg.content}</p>
                              )}
                            </div>
                          ) : msg.type === 'inquiry' && msg.products ? (
                            <div>
                              <strong style={{ color: '#ef4444', fontSize: 13, display: 'block', marginBottom: 6 }}>Product Inquiry</strong>
                              {Array.isArray(msg.products) && msg.products.map((p, i) => (
                                <div key={i} style={{ display: 'flex', gap: 10, marginTop: 8, alignItems: 'flex-start', paddingBottom: 8, borderBottom: i < msg.products.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                                  {p.image && (
                                    <img
                                      src={p.image.startsWith('http') ? p.image : `${API_BASE_URL}${p.image}`}
                                      alt=""
                                      style={{ width: 48, height: 48, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }}
                                    />
                                  )}
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 12, fontWeight: 500, color: '#1e293b', lineHeight: 1.4 }}>{p.name || p.description}</div>
                                    {p.price && <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{p.price}</div>}
                                  </div>
                                </div>
                              ))}
                              {msg.content && (
                                <p style={{ margin: '8px 0 0', fontSize: 13, color: '#334155' }}>{msg.content}</p>
                              )}
                            </div>
                          ) : (
                            <p style={{ margin: 0, fontSize: 13, color: '#334155' }}>{msg.content}</p>
                          )}
                        </div>
                        <span className="message-time" style={{ fontSize: 11, color: '#94a3b8' }}>
                          {formatMsgTime(msg.createdAt)}
                          {msg.isOwn ? ' · You' : ''}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
              <div className="chat-input">
                <input
                  type="text"
                  placeholder="Type a message as admin..."
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
                  disabled={sendingMessage}
                />
                <div className="chat-actions">
                  <button className="send-btn" onClick={handleSendMessage} disabled={sendingMessage || !messageText.trim()}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M2.01 21L23 12L2.01 3L2 10L17 12L2 14L2.01 21Z" fill="white"/>
                    </svg>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default Message2Page;
